import React from "react";
import { Modal, ModalBody, ModalHeader, Input, Button } from "reactstrap";
import {
  WhatsappIcon,
  FacebookIcon,
  EmailIcon,
  TelegramIcon,
} from "react-share";
import Icon from "Components/Icons/Icon";

interface CustomShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** URL to share (e.g. stripe_payment_link_url) */
  shareUrl: string;
  /** Modal header title */
  title?: string;
  /** Subject for email sharing */
  subject?: string;
  /** Custom share message — built by the parent and passed in */
  message: string;
  /** Recipient name shown in the share message (used if message not provided) */
  recipientName?: string;
  /** Recipient email pre-filled in the email input */
  recipientEmail?: string;
  /** Called when "Send Email" is clicked */
  onSendEmail?: () => void;
  /** Controls the loading state of the Send Email button */
  isSending?: boolean;
  /** Blocks all social share buttons (WhatsApp, Facebook, Email, Telegram, More) while true */
  isLoading?: boolean;
}

const CustomShareModal = ({
  isOpen,
  onClose,
  shareUrl,
  title = "Share",
  subject = "Shared Link",
  message,
  recipientName,
  recipientEmail = "",
  onSendEmail,
  isSending = false,
  isLoading = false,
}: CustomShareModalProps) => {
  const canUseTelegram = /Android|iPhone|iPad/i.test(navigator.userAgent);

  const SHARE_COUNT = canUseTelegram ? 5 : 4;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: message });
      } catch {
        // user cancelled
      }
    } else {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(message)}`,
        "_blank",
      );
    }
  };

  const handleTelegramShare = () => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(message);
    window.location.href = `tg://msg_url?url=${url}&text=${text}`;
    setTimeout(() => {
      window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
    }, 700);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="md"
      contentClassName="br-7"
    >
      <ModalHeader
        toggle={onClose}
        className="fw-medium fs-16 text-black2D p-3 bg-greyF5"
      >
        {title}
      </ModalHeader>

      <ModalBody className="p-3">
        {/* Share Icons */}
        <div className="mb-3">
          <div
            className="d-flex gap-3 pb-2 justify-content-evenly"
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              paddingBottom: "8px",
              scrollbarWidth: "thin",
            }}
          >
            {isLoading ? (
              Array.from({ length: SHARE_COUNT }).map((_, i) => (
                <div
                  key={i}
                  className="d-flex flex-column align-items-center gap-2"
                  style={{ minWidth: "56px" }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                    }}
                  />
                  <div
                    style={{
                      width: 44,
                      height: 10,
                      borderRadius: 4,
                      background:
                        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                    }}
                  />
                </div>
              ))
            ) : (
              <>
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                    message,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div
                    className="text-center"
                    style={{ cursor: "pointer", minWidth: "56px" }}
                  >
                    <WhatsappIcon size={56} round />
                    <div className="fs-12 mt-1">WhatsApp</div>
                  </div>
                </a>

                <a
                  href={`https://www.facebook.com/dialog/send?app_id=YOUR_APP_ID&link=${encodeURIComponent(
                    shareUrl,
                  )}&redirect_uri=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div
                    className="text-center"
                    style={{ cursor: "pointer", minWidth: "56px" }}
                  >
                    <FacebookIcon size={56} round />
                    <div className="fs-12 mt-1">Facebook</div>
                  </div>
                </a>

                <a
                  href={`mailto:?subject=${encodeURIComponent(
                    subject,
                  )}&body=${encodeURIComponent(message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="text-center" style={{ minWidth: "56px" }}>
                    <EmailIcon size={56} round />
                    <div className="fs-12 mt-1">Email</div>
                  </div>
                </a>

                {canUseTelegram && (
                  <div
                    className="text-center"
                    onClick={handleTelegramShare}
                    style={{ cursor: "pointer", minWidth: "56px" }}
                  >
                    <TelegramIcon size={56} round />
                    <div className="fs-12 mt-1">Telegram</div>
                  </div>
                )}

                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div
                    className="text-center bg-greyF5 rounded-circle d-flex flex-column align-items-center justify-content-center"
                    style={{
                      width: 56,
                      height: 56,
                      minWidth: "56px",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                    onClick={handleNativeShare}
                  >
                    <Icon name="ShareMore" width={24} height={24} />
                  </div>
                  <div className="fs-12 mt-1" style={{ whiteSpace: "nowrap" }}>
                    More
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Email Input */}
        {onSendEmail && (
          <div className="mb-3">
            <div className="email-share-wrapper d-flex align-items-center p-0 m-0 pe-1">
              <Input
                type="email"
                placeholder="Enter email address"
                value={recipientEmail}
                disabled
                className="email-input flex-grow-1"
              />
              <Button
                color="primary"
                onClick={onSendEmail}
                disabled={isSending}
                className="email-send-btn ms-2"
              >
                {isSending ? "Sending..." : "Send Email"}
              </Button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </ModalBody>
    </Modal>
  );
};

export default React.memo(CustomShareModal);
